export type Film = {
  id: string;
  title: string;
  original: string;
  countryKey: string;
  countryName: string;
  year: string;
  director: string;
  themes: string[];
  note: string;
};

export type CountryProfile = {
  key: string;
  name: string;
  en: string;
  tabLabel: string;
  lead: string;
  traits: string[];
  focus: string;
};

export type ThemeProfile = {
  key: string;
  name: string;
  en: string;
  description: string;
  films: string[];
};

export type FaqItem = {
  id: string;
  question: string;
  answer: string;
};

export const countryProfiles: CountryProfile[] = [
  {
    key: "france",
    name: "法国伦理电影",
    en: "France",
    tabLabel: "法国",
    lead:
      "在法国电影的脉络里，伦理很少被当成抽象说教，而是把人物放回家庭、欲望与阶级的现实处境中，让道德难题自然浮现。自新浪潮以来，法国作品偏爱开放的结局与留白式的情感表达，导演的个人视角往往比完整答案更重要。",
    traits: [
      "偏爱开放式结局与留白式叙事，把判断留给观众",
      "常以家庭与亲密关系作为道德实验场",
      "延续作者电影传统，强调导演的个人视角",
    ],
    focus: "从新浪潮到当代作者电影，法国作品习惯让日常细节承担沉重的伦理重量。",
  },
  {
    key: "italy",
    name: "意大利伦理电影",
    en: "Italy",
    tabLabel: "意大利",
    lead:
      "意大利伦理电影与新写实主义血脉相连：镜头对准战后普通人的谋生、尊严与挣扎，随后又分化出费里尼式的心理迷宫与维斯康蒂式的家族史诗。宗教传统与世俗现代的拉扯，是意大利作品里反复出现的张力来源。",
    traits: [
      "新写实主义传统，坚持普通人视角",
      "家族与阶层变迁被放进历史叙事",
      "宗教价值与世俗欲望之间的持续拉扯",
    ],
    focus: "贫困、尊严与家族命运，构成意大利伦理叙事最扎实的地基。",
  },
  {
    key: "spain",
    name: "西班牙伦理电影",
    en: "Spain",
    tabLabel: "西班牙",
    lead:
      "西班牙作品情感浓烈，常把母性、记忆与身份认同交织在一起。在佛朗哥时代结束后的创作里，历史创伤与个人秘密反复成为伦理叙事的入口，家庭既是避风港，也是秘密的容器。",
    traits: [
      "情感浓烈，戏剧张力饱满",
      "母性与家庭秘密是反复出现的母题",
      "历史创伤与个人记忆互为镜像",
    ],
    focus: "西班牙导演擅长用浓烈的情感外壳，包裹关于记忆与身份的追问。",
  },
  {
    key: "germany",
    name: "德国伦理电影",
    en: "Germany",
    tabLabel: "德国",
    lead:
      "德国伦理电影习惯直面历史罪责：监视、服从、规训与集体沉默构成其最核心的道德命题。叙事常冷峻、克制而富有自省意味，把个人选择放在制度与历史的压力下反复称量。",
    traits: [
      "直面历史罪责与集体记忆",
      "聚焦监视、服从与良知的张力",
      "冷峻克制的美学与叙事风格",
    ],
    focus: "在德国的叙事里，最沉重的伦理问题往往藏在最安静的日常里。",
  },
  {
    key: "uk",
    name: "英国伦理电影",
    en: "United Kingdom",
    tabLabel: "英国",
    lead:
      "英国作品擅长在社会现实的土壤里讨论道德：阶级、劳动与「体面」如何塑造人的选择，是许多佳作共同的出发点。它常以克制的日常对话推进冲突，把道德的重量压在一句不经意的对白上。",
    traits: [
      "社会现实主义底色，紧扣阶级议题",
      "对「体面」与羞耻的细腻观察",
      "以日常对话推动道德冲突",
    ],
    focus: "英国电影把伦理放进厨房与客厅，让阶级与体面成为看不见的裁判。",
  },
  {
    key: "other",
    name: "其他欧洲伦理电影",
    en: "Rest of Europe",
    tabLabel: "其他欧洲",
    lead:
      "跳出五大国，北欧的寒冷、东欧的转型与南欧的停滞各自孕育出不同的伦理追问：婚姻、劳动、信仰与生存尊严，在不同社会土壤里被重新命名。小语种作者电影为这些议题提供了更多元的表达方式。",
    traits: [
      "北欧：婚姻与存在主义的持续追问",
      "东欧：社会转型期的道德困境",
      "小语种作者电影的多元探索",
    ],
    focus: "欧洲的版图之外，仍是同一批关于人如何相处的古老问题。",
  },
];

export const films: Film[] = [
  {
    id: "fr-400",
    title: "四百击",
    original: "Les Quatre Cents Coups",
    countryKey: "france",
    countryName: "法国",
    year: "1959",
    director: "弗朗索瓦·特吕弗",
    themes: ["家庭与代际", "孤独与城市"],
    note: "一个被忽视的少年在逃离中走向海边，成长的代价成为最朴素的道德追问。",
  },
  {
    id: "fr-colors",
    title: "蓝白红三部曲",
    original: "Trois couleurs",
    countryKey: "france",
    countryName: "法国",
    year: "1993–1994",
    director: "克日什托夫·基耶斯洛夫斯基",
    themes: ["自由与规训", "生命与尊严"],
    note: "以自由、平等、博爱为命题，把抽象理念还原为具体人物的日常选择。",
  },
  {
    id: "fr-amour",
    title: "爱",
    original: "Amour",
    countryKey: "france",
    countryName: "法国",
    year: "2012",
    director: "迈克尔·哈内克",
    themes: ["家庭与代际", "生命与尊严"],
    note: "在一间公寓里，衰老与照护把一份承诺推向承受的极限。",
  },
  {
    id: "fr-adele",
    title: "阿黛尔的生活",
    original: "La Vie d'Adèle",
    countryKey: "france",
    countryName: "法国",
    year: "2013",
    director: "阿布戴·柯西胥",
    themes: ["性别与身份", "阶级与流动"],
    note: "一段关系里的激情、阶层差异与自我认同彼此纠缠，无法被轻易化解。",
  },
  {
    id: "fr-pick",
    title: "扒手",
    original: "Pickpocket",
    countryKey: "france",
    countryName: "法国",
    year: "1959",
    director: "罗贝尔·布列松",
    themes: ["生命与尊严", "孤独与城市"],
    note: "以极简手法探讨罪、救赎与自我审判，把动作背后的内心推到前台。",
  },
  {
    id: "it-bici",
    title: "偷自行车的人",
    original: "Ladri di biciclette",
    countryKey: "italy",
    countryName: "意大利",
    year: "1948",
    director: "维托里奥·德·西卡",
    themes: ["阶级与流动", "生命与尊严"],
    note: "一辆自行车牵出一个家庭在战后城市中的全部尊严与窘迫。",
  },
  {
    id: "it-dolce",
    title: "甜蜜的生活",
    original: "La Dolce Vita",
    countryKey: "italy",
    countryName: "意大利",
    year: "1960",
    director: "费德里科·费里尼",
    themes: ["孤独与城市", "性别与身份"],
    note: "浮华名利场中的精神空洞与道德失重，构成整部影片的暗流。",
  },
  {
    id: "it-rocco",
    title: "洛可兄弟",
    original: "Rocco e i suoi fratelli",
    countryKey: "italy",
    countryName: "意大利",
    year: "1960",
    director: "卢基诺·维斯康蒂",
    themes: ["家庭与代际", "阶级与流动"],
    note: "兄弟五人从南方到北方，家庭在贫困与欲望中一点点撕裂。",
  },
  {
    id: "it-strada",
    title: "大路",
    original: "La strada",
    countryKey: "italy",
    countryName: "意大利",
    year: "1954",
    director: "费德里科·费里尼",
    themes: ["孤独与城市", "生命与尊严"],
    note: "流浪艺人的相遇与失去，孤独成为整部作品最深的主题。",
  },
  {
    id: "it-gatt",
    title: "豹",
    original: "Il Gattopardo",
    countryKey: "italy",
    countryName: "意大利",
    year: "1963",
    director: "卢基诺·维斯康蒂",
    themes: ["历史与罪责", "阶级与流动"],
    note: "贵族在时代更替中的优雅退场与自我妥协，是历史给出的伦理答案。",
  },
  {
    id: "es-madre",
    title: "关于我母亲的一切",
    original: "Todo sobre mi madre",
    countryKey: "spain",
    countryName: "西班牙",
    year: "1999",
    director: "佩德罗·阿莫多瓦",
    themes: ["性别与身份", "家庭与代际"],
    note: "以母性为轴，把身份、宽容与和解编织进同一段故事。",
  },
  {
    id: "es-mar",
    title: "深海长眠",
    original: "Mar adentro",
    countryKey: "spain",
    countryName: "西班牙",
    year: "2004",
    director: "亚历杭德罗·阿梅纳瓦尔",
    themes: ["生命与尊严", "家庭与代际"],
    note: "一场关于生命尊严与自主选择的沉静讨论，拒绝给出简单的对错。",
  },
  {
    id: "es-fauno",
    title: "潘神的迷宫",
    original: "El laberinto del fauno",
    countryKey: "spain",
    countryName: "西班牙",
    year: "2006",
    director: "吉列尔莫·德尔·托罗",
    themes: ["历史与罪责", "孤独与城市"],
    note: "用童话的壳装载战争与服从的残酷命题，幻想成为逃避也是抵抗。",
  },
  {
    id: "es-volver",
    title: "回归",
    original: "Volver",
    countryKey: "spain",
    countryName: "西班牙",
    year: "2006",
    director: "佩德罗·阿莫多瓦",
    themes: ["家庭与代际", "性别与身份"],
    note: "女性家族中秘密、原谅与重生的循环，把伤痛转化为延续的力量。",
  },
  {
    id: "de-leben",
    title: "窃听风暴",
    original: "Das Leben der Anderen",
    countryKey: "germany",
    countryName: "德国",
    year: "2006",
    director: "弗洛里安·冯·多纳斯马克",
    themes: ["历史与罪责", "自由与规训"],
    note: "监视者的良知苏醒，成为这部作品最锋利也最安静的道德转折。",
  },
  {
    id: "de-himmel",
    title: "柏林苍穹下",
    original: "Der Himmel über Berlin",
    countryKey: "germany",
    countryName: "德国",
    year: "1987",
    director: "维姆·文德斯",
    themes: ["孤独与城市", "生命与尊严"],
    note: "以天使的视角凝视都市中的孤独与温柔，把关怀变成一种观看方式。",
  },
  {
    id: "de-band",
    title: "白丝带",
    original: "Das weiße Band",
    countryKey: "germany",
    countryName: "德国",
    year: "2009",
    director: "迈克尔·哈内克",
    themes: ["历史与罪责", "自由与规训"],
    note: "极端规训如何在小镇的土壤里悄悄孕育出暴力与沉默。",
  },
  {
    id: "de-lenin",
    title: "再见列宁",
    original: "Good Bye Lenin!",
    countryKey: "germany",
    countryName: "德国",
    year: "2003",
    director: "沃尔夫冈·贝克",
    themes: ["历史与罪责", "家庭与代际"],
    note: "用善意的谎言缝合家庭与历史的断裂，温柔里藏着难以言说的代价。",
  },
  {
    id: "uk-secrets",
    title: "秘密与谎言",
    original: "Secrets & Lies",
    countryKey: "uk",
    countryName: "英国",
    year: "1996",
    director: "迈克·李",
    themes: ["阶级与流动", "家庭与代际"],
    note: "一次相认撕开阶层与家庭的层层伪装，让被压抑的情绪终于出口。",
  },
  {
    id: "uk-train",
    title: "猜火车",
    original: "Trainspotting",
    countryKey: "uk",
    countryName: "英国",
    year: "1996",
    director: "丹尼·博伊尔",
    themes: ["孤独与城市", "阶级与流动"],
    note: "边缘青年在选择与沉沦之间的挣扎，把生存写成一场能量过剩的逃离。",
  },
  {
    id: "uk-shame",
    title: "羞耻",
    original: "Shame",
    countryKey: "uk",
    countryName: "英国",
    year: "2011",
    director: "史蒂夫·麦奎因",
    themes: ["性别与身份", "孤独与城市"],
    note: "都市孤独与成瘾如何一点点吞噬亲密的能力，是冷静而疼痛的观察。",
  },
  {
    id: "uk-45",
    title: "45 周年",
    original: "45 Years",
    countryKey: "uk",
    countryName: "英国",
    year: "2015",
    director: "安德鲁·海格",
    themes: ["家庭与代际", "孤独与城市"],
    note: "一封信让四十五年的婚姻忽然变得可疑，回忆成为最锋利的第三者。",
  },
  {
    id: "ot-scener",
    title: "婚姻生活",
    original: "Scener ur ett äktenskap",
    countryKey: "other",
    countryName: "瑞典",
    year: "1973",
    director: "英格玛·伯格曼",
    themes: ["家庭与代际", "性别与身份"],
    note: "把婚姻拆解为权力、依赖与语言的博弈，冷静得几乎令人无法回避。",
  },
  {
    id: "ot-jagten",
    title: "狩猎",
    original: "Jagten",
    countryKey: "other",
    countryName: "丹麦",
    year: "2012",
    director: "托马斯·温特伯格",
    themes: ["家庭与代际", "孤独与城市"],
    note: "一个指控如何让整个社群成为施害者，无辜在集体猜疑前不堪一击。",
  },
  {
    id: "ot-4luni",
    title: "四月三周两天",
    original: "4 luni, 3 săptămâni și 2 zile",
    countryKey: "other",
    countryName: "罗马尼亚",
    year: "2007",
    director: "克里斯蒂安·蒙吉",
    themes: ["自由与规训", "性别与身份"],
    note: "在制度的缝隙里，一次艰难的选择压垮了两个年轻人之间的关系。",
  },
  {
    id: "ot-deuxjours",
    title: "两天一夜",
    original: "Deux jours, une nuit",
    countryKey: "other",
    countryName: "比利时",
    year: "2014",
    director: "达内兄弟",
    themes: ["阶级与流动", "生命与尊严"],
    note: "让观众随主人公挨家挨户面对利益与良心，尊严在一次次敲门中被重新定义。",
  },
  {
    id: "ot-dogtooth",
    title: "狗牙",
    original: "Dogtooth",
    countryKey: "other",
    countryName: "希腊",
    year: "2009",
    director: "欧格斯·兰斯莫斯",
    themes: ["自由与规训", "家庭与代际"],
    note: "一个被制造的封闭世界，挑战规训、语言与认知的边界。",
  },
];

export const themeProfiles: ThemeProfile[] = [
  {
    key: "family",
    name: "家庭与代际",
    en: "Family & Generations",
    description:
      "封闭的家庭空间常被欧洲导演当作社会缩影：照护、隐瞒、继承与原谅，在饭桌与卧室之间反复上演。家庭既是情感的起点，也是伦理压力最集中的地方。",
    films: ["爱", "洛可兄弟", "回归", "45 周年"],
  },
  {
    key: "class",
    name: "阶级与流动",
    en: "Class & Mobility",
    description:
      "向上流动的渴望与根深蒂固的身份印记之间的冲突，构成了大量作品的动力。人物往往不是被善恶驱动，而是被生存位置推着做选择。",
    films: ["偷自行车的人", "阿黛尔的生活", "秘密与谎言", "两天一夜"],
  },
  {
    key: "history",
    name: "历史与罪责",
    en: "History & Guilt",
    description:
      "战争、分裂与专制留下的创伤，通过个人记忆重新被讲述。作品追问的不是「谁有罪」，而是普通人如何在历史压力下保持或放弃良知。",
    films: ["窃听风暴", "白丝带", "潘神的迷宫", "再见列宁"],
  },
  {
    key: "freedom",
    name: "自由与规训",
    en: "Freedom & Discipline",
    description:
      "制度、家庭与语言共同塑造着「正常」的标准。这类作品关注人如何被规训，以及在何种代价下才能挣脱被规定好的生活。",
    films: ["蓝白红三部曲", "狗牙", "四月三周两天", "白丝带"],
  },
  {
    key: "solitude",
    name: "孤独与城市",
    en: "Solitude & the City",
    description:
      "城市提供了密集的人群，却不一定提供联结。作品把镜头对准那些身处人群却被忽略的人，让孤独成为可以被看见的现实。",
    films: ["四百击", "甜蜜的生活", "柏林苍穹下", "羞耻"],
  },
  {
    key: "identity",
    name: "性别与身份",
    en: "Gender & Identity",
    description:
      "性别角色与自我认同常常在亲密关系中被重新定义。作品关心的是：当既定的身份期待被打破，人和关系将如何重新寻找位置。",
    films: ["关于我母亲的一切", "婚姻生活", "阿黛尔的生活", "关于我母亲的一切"],
  },
  {
    key: "dignity",
    name: "生命与尊严",
    en: "Life & Dignity",
    description:
      "衰老、疾病与死亡让「什么是有尊严的生活」变成了必须回答的问题。这类作品极少给出答案，而是把选择的重压交还给人物与观众。",
    films: ["爱", "深海长眠", "偷自行车的人", "两天一夜"],
  },
];

export const faqItems: FaqItem[] = [
  {
    id: "faq-1",
    question: "「欧美伦理电影」和一般意义上的伦理片，是一回事吗？",
    answer:
      "这里所说的伦理电影，指以道德困境为核心议题的严肃剧情作品，重点在家庭、阶级、历史罪责、身份认同等现实命题。它更接近欧洲的作者电影与艺术电影传统，关注的是人物在具体处境中的选择，而不是把道德简化成非黑即白的结论。",
  },
  {
    id: "faq-2",
    question: "法国、意大利、西班牙的伦理电影各有什么不同侧重？",
    answer:
      "法国作品偏爱留白与开放式结局，把判断留给观众，关注家庭与亲密关系中的微妙张力；意大利作品与新写实主义一脉相承，常从普通人的谋生与尊严切入，并延伸到家族史诗；西班牙作品情感浓烈，把母性、记忆与历史创伤交织在一起，戏剧张力更饱满。",
  },
  {
    id: "faq-3",
    question: "德国与英国的伦理电影，关注点主要差在哪里？",
    answer:
      "德国作品更偏向历史与制度维度，反复处理监视、服从、规训与集体沉默，叙事冷峻而自省；英国作品则更贴近社会现实，围绕阶级、劳动与「体面」展开，常用克制的日常对话推进冲突。一个向历史追问，一个向当下观察。",
  },
  {
    id: "faq-4",
    question: "如果第一次接触这类电影，应该从哪里开始？",
    answer:
      "可以先按地区选一部风格较清晰的代表作：想看家庭与情感，从法国或西班牙入手；想看社会与尊严，从意大利或英国入手；想理解历史与良知，从德国或东欧作品入手。也可以按本站的「主题母题」分区，先挑一个最关心的话题，再找它对应的作品。",
  },
  {
    id: "faq-5",
    question: "这些作品为什么常常以家庭作为叙事的切口？",
    answer:
      "家庭是伦理关系最密集的场所：照护与责任、隐瞒与坦白、继承与背叛，都在其中同时存在。对导演来说，把宏大的社会与历史议题收进一个家庭的空间，能让抽象的道德问题变得具体、可感，也更容易让观众代入。",
  },
];